import { useCallback, useContext } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  AccountCollection,
  AccountData,
  ClientCollection,
  RecurringInvestmentTransactionCollection,
  RecurringInvestmentTransactionOccurrence,
  RecurringInvestmentTransactionOccurrenceCollection,
} from "wealthmode-frontend/backend"
import { Box, HStack, Text } from "wealthmode-frontend/gluestack/ui"
import { formatMoneyFull } from "wealthmode-frontend/util"

import { DataTable } from "@/components"
import { BackendContext } from "@/context"

export default function RecurringInvestmentTransactionOccurrences() {
  const { client_id, account_id, recurring_investment_transaction_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
    recurring_investment_transaction_id: string
  }>()

  const backend = useContext(BackendContext)

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection
    const account = accounts.model(account_id)
    await account.fetch({ reload: true })

    const data = account.embed("data") as AccountData
    const transactions = data.link("recurring_investment_transactions")?.resolve() as
      RecurringInvestmentTransactionCollection | undefined
    if (!transactions) {
      return undefined
    }

    const transaction = transactions.model(recurring_investment_transaction_id)
    await transaction.fetch({ reload: true })

    const occurrences = transaction.link("occurrences")?.resolve() as
      RecurringInvestmentTransactionOccurrenceCollection | undefined
    if (!occurrences) {
      return undefined
    }

    await occurrences.fetch({ reload: true })

    return occurrences
  }, [backend, client_id, account_id, recurring_investment_transaction_id])

  return (
    <Box>
      <HStack>
        <Box className="flex-1">
          <DataTable
            title={`Occurrences of Recurring Investment Transaction ${recurring_investment_transaction_id}`}
            load={load}
            header={[
              { id: "id", title: "ID", shrink: true },
              { id: "date", title: "Date" },
              { id: "transaction", title: "Transaction", shrink: true },
              { id: "category", title: "Category" },
              { id: "kind", title: "Kind" },
              { id: "description", title: "Description" },
              { id: "quantity", title: "Quantity" },
              { id: "price", title: "Price" },
              { id: "cash_delta", title: "Cash Delta" },
              { id: "value_delta", title: "Value Delta" },
              { id: "estimated_account_value", title: "Est. Account Balance" },
              { id: "estimated_holdings", title: "Est. Holdings" },
            ]}
            render={(model: RecurringInvestmentTransactionOccurrence, field: string) => {
              const transaction = model.investmentTransaction()

              switch (field) {
                case "transaction": {
                  const transactionID = transaction?.attributes.id
                  if (!transactionID) {
                    return ""
                  }

                  return (
                    <Link
                      href={`/clients/${client_id}/accounts/${account_id}/investment_transactions/${transactionID}`}
                    >
                      <Text className="text-markdown-link hover:opacity-80 underline whitespace-nowrap">
                        {transactionID}
                      </Text>
                    </Link>
                  )
                }

                case "date":
                case "category":
                case "kind":
                case "description":
                case "quantity":
                  return transaction?.get(field) ?? ""

                case "price":
                case "cash_delta":
                case "value_delta": {
                  const value = transaction?.get(field)
                  return value == null ? "" : formatMoneyFull(value)
                }

                case "estimated_account_value": {
                  const value = model.attributes.estimated_account_value
                  return value == null ? "" : formatMoneyFull(value)
                }

                case "estimated_holdings":
                  return model.attributes.estimated_holdings ?? ""
              }
              return model.get(field)
            }}
          />
        </Box>
      </HStack>
    </Box>
  )
}
